import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useTranslate } from '../../../components/TranslationText';
import TranslationsKeys from '../../../TranslationsKeys';
import {
    getWorkshopRegistrationDefaultValues,
    buildWorkshopRegistrationFormData,
} from '../utils/workshopRegistrationMappers';
import { WorkshopRegistrationConstants } from '../utils/workshopRegistrationConstants';
import { registerWorkshopParticipant } from '../Api/workshopRegistrationApi';

/**
 * Controller hook for Workshop Registration.
 * Encapsulates form lifecycle, validation state, API orchestration, and user feedback.
 */
export function useWorkshopRegistration() {
    const { translate } = useTranslate();

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submittedName, setSubmittedName] = useState('');

    const methods = useForm({
        defaultValues: getWorkshopRegistrationDefaultValues(),
        mode: 'onTouched',
    });

    const {
        handleSubmit,
        reset,
        formState: { isSubmitting },
    } = methods;

    const onSubmit = async (data) => {
        try {
            const formData = buildWorkshopRegistrationFormData(data);
            const responseData = await registerWorkshopParticipant(formData);

            const responseCode = responseData?.responseCode ?? responseData?.status?.responseCode;

            // When responseCode is not 200, show the error and do not advance to success
            if (responseCode !== undefined && responseCode !== null && String(responseCode) !== '200') {
                const errorMsg =
                    responseData?.responseText ||
                    responseData?.result?.message ||
                    responseData?.message ||
                    responseData?.status?.detailedResponseCode ||
                    responseData?.status?.message ||
                    translate(
                        TranslationsKeys.ERROR_IN_SUBMITTING, 'An error occurred during submission, please try again later'
                    );

                toast.error(translate(errorMsg, errorMsg));
                return;
            }

            const successMsg =
                responseData?.responseText ||
                responseData?.result?.message ||
                responseData?.status?.detailedResponseCode ||
                responseData?.message ||
                translate(
                    TranslationsKeys.REGISTRATION_SUCCESS,'Registration submitted successfully!'
                );

            toast.success(translate(successMsg, successMsg));
            setSubmittedName(data[WorkshopRegistrationConstants.FULL_NAME] || '');
            setIsSubmitted(true);
        } catch (error) {
            console.error('Workshop registration error:', error);
            const errorData = error?.response?.data || error?.data;
            const errorMsg =
                errorData?.responseText ||
                errorData?.result?.message ||
                errorData?.message ||
                errorData?.status?.detailedResponseCode ||
                errorData?.status?.message ||
                errorData?.title ||
                error?.message ||
                translate(
                    TranslationsKeys.ERROR_IN_SUBMITTING,'An error occurred during submission, please try again later'
                );

            toast.error(translate(errorMsg, errorMsg));
        }
    };

    const handleReset = () => {
        reset(getWorkshopRegistrationDefaultValues());
        setIsSubmitted(false);
        setSubmittedName('');
    };

    return {
        methods,
        handleSubmit,
        onSubmit,
        handleReset,
        isSubmitted,
        submittedName,
        isSubmitting,
        translate,
    };
}

export default useWorkshopRegistration;
